import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/lib/auth";
import { Toaster } from "@/components/ui/sonner";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import ResetPassword from "@/pages/ResetPassword";
import VerifyEmail from "@/pages/VerifyEmail";
import Register from "@/pages/Register";
import AppLayout from "@/components/AppLayout";
import Dashboard from "@/pages/Dashboard";
import Calculators from "@/pages/Calculators";
import DeadlinesPage from "@/pages/Deadlines";
import Vault from "@/pages/Vault";
import Chat from "@/pages/Chat";
import Guides from "@/pages/Guides";
import Generator from "@/pages/Generator";
import Bandi from "@/pages/Bandi";
import Settings from "@/pages/Settings";
import Transactions from "@/pages/Transactions";
import Audit from "@/pages/Audit";
import Eventi from "@/pages/Eventi";
import Admin from "@/pages/Admin";
import AdminFeedback from "@/pages/AdminFeedback";
import Privacy from "@/pages/Privacy";
import Terms from "@/pages/Terms";
import Pitch from "@/pages/Pitch";
import CookieBanner from "@/components/CookieBanner";
import InstallBanner from "@/components/InstallBanner";
import ErrorBoundary from "@/components/ErrorBoundary";
import { useLocation } from "react-router-dom";
import { useEffect } from "react";

/** Cattura globale del parametro ?invito=XXX per il tracciamento del canale di acquisizione.
 * Salva in localStorage (chiave 'tuotax_invito') alla prima visita su QUALSIASI pagina.
 * Sovrascrive solo se il nuovo valore rispetta il formato (max 32 char, alfanum + trattini).
 */
function InviteCapture() {
  const { search } = useLocation();
  useEffect(() => {
    try {
      const params = new URLSearchParams(search);
      const raw = (params.get("invito") || "").trim();
      if (!raw) return;
      // Valida: max 32 caratteri, alfanumerico + trattini
      if (/^[A-Za-z0-9-]{1,32}$/.test(raw)) {
        localStorage.setItem("tuotax_invito", raw);
      }
    } catch {}
  }, [search]);
  return null;
}

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="p-12 text-muted-foreground">Caricamento...</div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/login" element={<Login />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/verify" element={<VerifyEmail />} />
            <Route path="/register" element={<ErrorBoundary><Register /></ErrorBoundary>} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/pitch" element={<Pitch />} />
            <Route path="/app" element={<Protected><AppLayout /></Protected>}>
              <Route index element={<Dashboard />} />
              <Route path="bandi" element={<Bandi />} />
              <Route path="calcolatori" element={<Calculators />} />
              <Route path="scadenze" element={<DeadlinesPage />} />
              <Route path="archivio" element={<Vault />} />
              <Route path="chat" element={<Chat />} />
              <Route path="guide" element={<Guides />} />
              <Route path="generatore" element={<Generator />} />
              <Route path="movimenti" element={<Transactions />} />
              <Route path="verifica" element={<Audit />} />
              <Route path="eventi" element={<Eventi />} />
              <Route path="impostazioni" element={<Settings />} />
              {/* Route legacy /dati e /app/dati: redirect a Impostazioni (contiene già Export dati JSON + Elimina account) */}
              <Route path="dati" element={<Navigate to="/app/impostazioni" replace />} />
              <Route path="admin" element={<Admin />} />
              <Route path="admin/feedback" element={<AdminFeedback />} />
              {/* Catch-all interno all'app: se atterrano su un path inesistente sotto /app, li portiamo alla dashboard */}
              <Route path="*" element={<Navigate to="/app" replace />} />
            </Route>
            {/* Root catch-all: qualunque route root non riconosciuta (incluso /dati legacy) → home */}
            <Route path="/dati" element={<Navigate to="/app/impostazioni" replace />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <InviteCapture />
          <ConditionalBanners />
        </BrowserRouter>
        <Toaster position="top-right" richColors visibleToasts={3} duration={4000} closeButton />
      </AuthProvider>
    </ErrorBoundary>
  );
}

// Nasconde cookie/install banner sulla pagina /pitch (esperienza deck pulita per investitori)
// e sulle pagine auth (register/login) per non intralciare i CTA su mobile.
function ConditionalBanners() {
  const { pathname } = useLocation();
  const hideOn = ["/pitch", "/register", "/login", "/reset-password", "/verify"];
  if (hideOn.includes(pathname)) return null;
  return <CookieBanner />;
}

export default App;
